import { Plus, Briefcase, Trash2 } from "lucide-react";
import React, { useState } from "react";

import StaffManagerModal, { StaffManagerType } from "./staff-manager-modal";

interface StaffManagerListProps {
  staffManagers: StaffManagerType[];
  onStaffManagersChange: (staffManagers: StaffManagerType[]) => void;
}

const StaffManagerList: React.FC<StaffManagerListProps> = ({
  staffManagers,
  onStaffManagersChange
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const resolveRowKey = (item: StaffManagerType) =>
    item.id ?? `${item.userId}-${item.roleId}`;

  const handleAddStaff = (newItems: StaffManagerType[]) => {
    onStaffManagersChange([...staffManagers, ...newItems]);
  };

  const handleDeleteStaff = (staff: StaffManagerType) => {
    const targetKey = resolveRowKey(staff);
    const updatedStaff = staffManagers.filter(
      (s) => resolveRowKey(s) !== targetKey
    );
    onStaffManagersChange(updatedStaff);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  return (
    <div className="mt-10 px-4">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">نقش‌های سازمان</h3>
        <button
          type="button"
          onClick={handleOpenModal}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          <Plus size={18} />
          افزودن نقش
        </button>
      </div>

      {staffManagers.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12">
          <div className="text-center">
            <Briefcase className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="mb-4 text-gray-500">هیچ نقشی اضافه نشده است</p>
            <button
              onClick={handleOpenModal}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-100 px-4 py-2 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-200"
            >
              <Plus size={18} />
              افزودن اولین نقش
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="divide-y divide-gray-200">
            {staffManagers.map((staff) => (
              <div
                key={resolveRowKey(staff)}
                className="p-4 transition-colors hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-500">
                    <Briefcase className="text-white" size={24} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900">
                      {staff.userName || staff.roleName}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-gray-400">
                      {staff.roleName}
                    </p>
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleDeleteStaff(staff)}
                      className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600"
                      title="حذف"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <StaffManagerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddStaff}
        title="افزودن نقش‌ها"
        existingRoles={staffManagers}
      />
    </div>
  );
};

export default StaffManagerList;
